import React from 'react';

/**
 * VideoEmbed — lazy "facade" YouTube embed.
 *
 * Shows the video thumbnail + a play button. The heavy YouTube iframe (and its
 * ~1MB of player JS) loads ONLY when the user clicks play, so it never weighs
 * down initial page load or Core Web Vitals. Dependency-free.
 *
 * Usage: <VideoEmbed videoId="YBzPWm1EcjU" title="Talebound Tutorial" />
 */
export default function VideoEmbed({ videoId, title = 'Video', maxWidth = 760 }) {
  const [playing, setPlaying] = React.useState(false);
  const maxres = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const hq = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div style={{ maxWidth, width: '100%', margin: '0 auto' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9
          borderRadius: 14,
          overflow: 'hidden',
          background: '#0f172a',
          boxShadow: '0 10px 34px rgba(15,23,42,0.20)',
        }}
      >
        {playing ? (
          <iframe
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${title}`}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              padding: 0,
              border: 0,
              cursor: 'pointer',
              background: 'transparent',
            }}
          >
            <img
              src={maxres}
              alt=""
              onError={(e) => { if (e.currentTarget.src !== hq) e.currentTarget.src = hq; }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 76,
                height: 76,
                borderRadius: '50%',
                background: 'rgba(15,23,42,0.72)',
                boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
